import { ADVISOR_ENGAGEMENT_SOURCE_TEAM } from '@src/constants/advisor-engagement-sources'
import { ScoAdvisorEngagement } from '@src/graphql-types'

const chatLastActiveMs = (engagement: ScoAdvisorEngagement) => {
  const raw = engagement.chatThread?.lastActiveAt
  if (!raw) {
    return 0
  }
  const ms = new Date(raw).getTime()
  return Number.isFinite(ms) ? ms : 0
}

const hasUnread = (engagement: ScoAdvisorEngagement) =>
  (engagement.chatThread?.currentUserUnreadCommentsCount ?? 0) > 0

const isTeamEngagement = (engagement: ScoAdvisorEngagement) =>
  !engagement.isAI && engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_TEAM

const teamSortName = (engagement: ScoAdvisorEngagement) =>
  engagement.advisorUser?.fullName || engagement.advisorUser?.displayName || engagement.advisorName || ''

const compareNames = (a: ScoAdvisorEngagement, b: ScoAdvisorEngagement) =>
  teamSortName(a).localeCompare(teamSortName(b), undefined, {
    sensitivity: 'base',
  })

/**
 * Activity ordering for non-team board advisors (client-side board lists only).
 * My Founders is sorted server-side via orderBy: lastActivity — do not use this there.
 *
 * 1. Wil (AI) first when present
 * 2. Threads with unreads
 * 3. Most-recent attached chat activity
 */
const compareEngagementsByChatActivity = (a: ScoAdvisorEngagement, b: ScoAdvisorEngagement): number => {
  if (!!a.isAI !== !!b.isAI) {
    return a.isAI ? -1 : 1
  }

  const aUnread = hasUnread(a)
  const bUnread = hasUnread(b)
  if (aUnread !== bUnread) {
    return aUnread ? -1 : 1
  }

  return chatLastActiveMs(b) - chatLastActiveMs(a)
}

/**
 * My Advisors sidebar / View All ordering (small board lists, client-side):
 * 1. Wil.ai (isAI) always first
 * 2. Startups.com team (sourceKey team), alphabetically by display name
 * 3. Everyone else by chat activity (unreads, then recency)
 */
export const sortAdvisorBoardEngagements = <T extends ScoAdvisorEngagement>(engagements: T[]): T[] => {
  const wil: T[] = []
  const team: T[] = []
  const rest: T[] = []
  for (const engagement of engagements) {
    if (engagement.isAI) {
      wil.push(engagement)
    } else if (isTeamEngagement(engagement)) {
      team.push(engagement)
    } else {
      rest.push(engagement)
    }
  }

  team.sort(compareNames)
  // Non-team humans only — Wil already extracted, so isAI tie-break is a no-op here.
  rest.sort(compareEngagementsByChatActivity)
  return [...wil, ...team, ...rest]
}

const engagementKey = (engagement: ScoAdvisorEngagement) =>
  engagement.advisorEngagementId ||
  engagement.id ||
  engagement.advisorUserId ||
  engagement.advisorUser?.userId ||
  engagement.founderUserId ||
  engagement.founderUser?.userId ||
  null

/**
 * Take the top `limit` sorted engagements for the sidebar, but always include the
 * currently open engagement even when it falls outside that window (appended after
 * the top results so the ranked list stays intact).
 */
export const takeSidebarEngagements = <T extends ScoAdvisorEngagement>(
  sorted: T[],
  limit: number,
  isPinned: (engagement: T) => boolean
): T[] => {
  const limited = sorted.slice(0, limit)
  const pinned = sorted.find(isPinned)
  if (!pinned) {
    return limited
  }

  const pinnedKey = engagementKey(pinned)
  const alreadyIncluded = limited.some(engagement => {
    if (engagement === pinned) {
      return true
    }
    const key = engagementKey(engagement)
    return !!pinnedKey && key === pinnedKey
  })
  if (alreadyIncluded) {
    return limited
  }

  return [...limited, pinned]
}
